import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Payment as PaymentIcon } from '@mui/icons-material';
import api from '../services/api';

function formatCurrency(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatFuelLine(jobLine) {
  const parts = [];
  if (jobLine.diesel_gallons > 0) parts.push(`${jobLine.diesel_gallons} gal Diesel`);
  if (jobLine.def_gallons > 0) parts.push(`${jobLine.def_gallons} gal DEF`);
  return parts.length ? parts.join(' / ') : '—';
}

function getStatusColor(status) {
  const colors = {
    submitted: 'warning',
    paid: 'success',
    partial: 'info',
    overdue: 'error',
    pending: 'default',
    cancelled: 'default',
  };
  return colors[status] || 'default';
}

async function downloadInvoicePdf(invoice) {
  if (!invoice?.id) return;
  const response = await api.get(`/invoices/${invoice.id}/download_pdf/`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoice_number || invoice.id}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function InvoiceDetailsDialog({
  open,
  onClose,
  invoiceId = null,
  invoice: initialInvoice = null,
  showCustomer = false,
  showPublicPayLink = false,
  onPay = null,
  isMobile = false,
}) {
  const [invoice, setInvoice] = useState(initialInvoice);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      setError('');
      return;
    }

    const id = invoiceId || initialInvoice?.id;
    if (!id) {
      setInvoice(initialInvoice);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/invoices/${id}/`);
        if (!cancelled) setInvoice(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              'Failed to load invoice details.'
          );
          setInvoice(initialInvoice);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId, initialInvoice]);

  const handleDownload = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const jobLines = invoice?.jobs || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{ '& .MuiDialog-paper': { m: { xs: 0, sm: 2 } } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            Invoice {invoice?.invoice_number || ''}
          </Typography>
          {invoice?.status && (
            <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {invoice && (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                {showCustomer && invoice.customer_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Customer:</strong> {invoice.customer_name}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Created:</strong> {formatDateTime(invoice.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Due date:</strong> {formatDate(invoice.due_date)}
                </Typography>
                {invoice.paid_at && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Paid:</strong> {formatDateTime(invoice.paid_at)}
                  </Typography>
                )}

                <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Subtotal:</strong> {formatCurrency(invoice.amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Tax:</strong> {formatCurrency(invoice.tax)}
                  </Typography>
                  {invoice.credit_card_surcharge_amount != null &&
                    parseFloat(invoice.credit_card_surcharge_amount) > 0 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>
                          Card fee ({invoice.credit_card_surcharge_percent || 0}%):
                        </strong>{' '}
                        {formatCurrency(invoice.credit_card_surcharge_amount)}
                      </Typography>
                    )}
                  <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
                    Total: {formatCurrency(invoice.total_amount)}
                  </Typography>
                </Box>

                {showPublicPayLink && invoice.public_pay_url && (
                  <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-all' }}>
                    <strong>Public pay link:</strong>{' '}
                    <Link href={invoice.public_pay_url} target="_blank" rel="noopener noreferrer">
                      {invoice.public_pay_url}
                    </Link>
                  </Typography>
                )}

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Line items ({jobLines.length})
                </Typography>
                {jobLines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No jobs on this invoice.
                  </Typography>
                ) : (
                  <TableContainer sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Location</TableCell>
                          <TableCell>Fuel</TableCell>
                          <TableCell>Delivery notes</TableCell>
                          <TableCell align="right">Delivery fee</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobLines.map((line) => (
                          <TableRow key={line.id || line.job_id}>
                            <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>
                              {line.job_location || '—'}
                            </TableCell>
                            <TableCell>{formatFuelLine(line)}</TableCell>
                            <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap' }}>
                              {line.delivery_notes?.trim() ? line.delivery_notes : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(line.delivery_fee)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {invoice?.pdf_file_url && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
          >
            Download PDF
          </Button>
        )}
        {onPay && invoice?.status !== 'paid' && (
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => onPay(invoice)}
          >
            Pay
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
